/**
 * Segment a string into an array of graphemes.
 * @param input
 * @returns
 */
export function segmentString(input: string) {
  // If supported use Intl's segmenter.
  // Need to cast to 'any' because TS doesn't know about Intl.Segmenter.
  const intl = Intl as any;
  if (intl?.Segmenter) {
    const segmenter = new intl.Segmenter();
    if (segmenter.segment) {
      const segmentsArray = Array.from(segmenter.segment(input)) as {
        segment: string;
      }[];
      return segmentsArray.map(({ segment }) => segment);
    }
  }

  // Otherwise use `Array.from`. Which doesn't cover all cases.
  return Array.from(input);
}

/**
 * Get the real length of a string. Considers graphemes.
 * @param input
 * @returns
 */
export function getRealLength(input: string) {
  return segmentString(input).length;
}

/**
 * Add line breaks to japanese text.
 * @param text - text to be broken
 * @param desiredLineLength - desired line length
 * @returns - wrapped text, lines can be longer than desiredLineLength if there is no space to break
 */
export function breakJapaneseText(
  text: string,
  desiredLineLength: number = 16,
) {
  // Characters that cannot be broken.
  const blacklistedCharacters =
    ',)]｝、〕〉》」』】〙〗〟’”｠»ゝゞーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇷ゚ㇺㇻㇼㇽㇾㇿ々〻‐゠–〜～?!‼⁇⁈⁉・:;/。.';

  // Current character index.
  let cursor = desiredLineLength;

  // Do not modify the original string.
  let result = text.slice();

  while (cursor < result.length) {
    // While current character is blacklisted, move the cursor forward.
    while (blacklistedCharacters.includes(result[cursor])) cursor++;
    // Hit the end of the string, break.
    if (cursor >= result.length) break;

    // Add line break.
    result =
      result.slice(0, cursor).trim() + // Trim to remove trailing spaces.
      '\n' + // Add line break.
      result.slice(cursor, result.length).trim(); // Trim to remove leading spaces.

    // Move the cursor to the desired end of next line.
    cursor = result.lastIndexOf('\n') + desiredLineLength;
  }

  // Return new text.
  return result;
}

/**
 * Check if message is in japanese.
 * This can yield false positives, if the message is in english but contains japanese characters.
 * @param input
 * @returns
 */
export function isJapanese(input: string) {
  return !!input?.match(/[\u3040-\u30ff]/);
}
